import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Inbox as InboxIcon } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate, toUtc } from "@/lib/mood";
import { NoteThread } from "@/components/NoteThread";
import { logError } from "@/lib/log";
import { ThankButton } from "@/components/ThankButton";

export const INBOX_EVENT = "mm-inbox-changed";

const when = (d) => fmtDate(toUtc(d), { month: "short", day: "numeric" });

const Row = ({ c, active, onOpen }) => (
  <button onClick={() => onOpen(c.id)} data-testid={`inbox-row-${c.id}`}
    className={`w-full text-left px-4 py-3 flex gap-3 items-start transition-colors hover:bg-mm-soft ${active ? "bg-mm-soft" : ""}`}>
    <div className="w-10 h-10 rounded-full mm-gradient text-white flex items-center justify-center font-bold shrink-0">{c.supporter_name[0]?.toUpperCase()}</div>
    <div className="flex-1 min-w-0">
      <div className="flex justify-between gap-2">
        <span className={`truncate ${c.unread ? "font-bold" : "font-medium"}`}>
          {c.supporter_name}
          {c.supporter_streak > 0 && <span className="ml-1.5 text-[11px] font-semibold text-mm-warning whitespace-nowrap" title={`${c.supporter_streak}-week streak`} data-testid={`inbox-streak-${c.id}`}>🔥 {c.supporter_streak}-week streak</span>}
        </span>
        <span className="text-xs text-mm-muted whitespace-nowrap">{when(c.last_at)}</span>
      </div>
      <div className="flex justify-between gap-2 items-center">
        <span className={`text-sm truncate ${c.unread ? "text-mm-text" : "text-mm-muted"}`}>{c.last_sender === "user" ? "You: " : ""}{c.last_message}</span>
        {c.unread > 0 && <span className="text-[11px] font-bold text-white mm-gradient rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center" data-testid={`inbox-unread-${c.id}`}>{c.unread}</span>}
      </div>
    </div>
  </button>
);

const Thread = ({ id, onBack, onChange }) => {
  const [note, setNote] = useState(null);
  useEffect(() => {
    setNote(null);
    api.get(`/inbox/${id}`).then((r) => {
      setNote(r.data);
      api.post(`/supporter-notes/${id}/read`).then(onChange).catch((e) => logError("Mark read failed:", e));
    }).catch((e) => logError("Thread load failed:", e));
  }, [id, onChange]);

  const heartBack = async () => {
    try { await api.post(`/supporter-notes/${id}/heart`); setNote((n) => ({ ...n, hearted: true })); toast.success(`Heart sent to ${note.supporter_name} ♥`); }
    catch (e) { toast.error(errMsg(e, "Could not send heart")); }
  };
  const thanked = (d) => { setNote((n) => ({ ...n, thread: d.thread, thanked: d.thanked })); onChange(); };
  if (!note) return <div className="p-6 text-mm-muted text-sm">Loading…</div>;
  return (
    <div className="p-5" data-testid="inbox-thread">
      <button onClick={onBack} className="lg:hidden text-sm text-mm-muted flex items-center gap-1 mb-3" data-testid="inbox-back"><ArrowLeft size={15} />All conversations</button>
      <div className="font-semibold" data-testid="inbox-thread-name">{note.supporter_name}</div>
      <div className="mm-soft rounded-2xl rounded-bl-sm px-3 py-2 mt-3 text-sm max-w-[85%] whitespace-pre-wrap break-words" data-testid="inbox-thread-note">
        <span className="text-[11px] text-mm-muted block">{note.supporter_name} · {when(note.created_at)}</span>{note.message}
        {(note.cheer_for || note.video_id) && (note.hearted
          ? <div className="mt-1.5 text-[11px] text-mm-primary font-semibold" data-testid="inbox-hearted-back">♥ Hearted back</div>
          : <div className="mt-1.5"><button className="mm-chip !py-1 !px-2.5 !text-[11px]" onClick={heartBack} data-testid="inbox-heart-back">♥ Heart back</button></div>)}
        {note.tough_msgs?.includes(0) && <div className="mt-1.5"><ThankButton noteId={note.id} index={0} name={note.supporter_name} done={note.thanked?.includes(0)} hearted={note.thanks_hearted?.includes(0)} onDone={thanked} label={note.kind === "hug" ? "Thank you for the hug 💜" : undefined} /></div>}
      </div>
      <NoteThread note={note} me="user" otherName={note.supporter_name} endpoint={`/supporter-notes/${id}/reply`}
        onUpdate={(_, thread) => { setNote((n) => ({ ...n, thread })); onChange(); }} onThanked={thanked} />
    </div>
  );
};

export default function Inbox() {
  const [list, setList] = useState(null);
  const [params, setParams] = useSearchParams();
  const open = params.get("c");

  const load = useCallback(() => {
    api.get("/inbox").then((r) => setList(r.data)).catch((e) => logError("Inbox load failed:", e));
    window.dispatchEvent(new Event(INBOX_EVENT));
  }, []);
  useEffect(() => { load(); }, [load]);

  const total = (list || []).reduce((s, c) => s + c.unread, 0);
  return (
    <div className="mm-fade-up" data-testid="inbox-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-4xl sm:text-5xl font-extrabold">Inbox</h1>
        <Link to="/hugs" className="mm-chip !text-sm" data-testid="inbox-hugs-link">🤗 My Hugs</Link>
      </div>
      <p className="text-mm-muted mt-2" data-testid="inbox-summary">{total ? `${total} unread message${total > 1 ? "s" : ""} from your supporters` : "You're all caught up"}</p>
      {list && !list.length ? (
        <div className="mm-card p-10 mt-8 text-center text-mm-muted" data-testid="inbox-empty">
          <InboxIcon className="mx-auto mb-3" size={32} />No conversations yet. Notes from your supporters will appear here.
        </div>
      ) : (
        <div className="mm-card mt-8 overflow-hidden lg:grid lg:grid-cols-[320px_1fr] min-h-[420px]">
          <div className={`divide-y lg:border-r ${open ? "hidden lg:block" : ""}`} data-testid="inbox-list">
            {(list || []).map((c) => <Row key={c.id} c={c} active={c.id === open} onOpen={(id) => setParams({ c: id })} />)}
          </div>
          <div className={open ? "" : "hidden lg:flex items-center justify-center text-mm-muted text-sm"}>
            {open ? <Thread id={open} onBack={() => setParams({})} onChange={load} /> : "Pick a conversation"}
          </div>
        </div>
      )}
    </div>
  );
}
